import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import './Preloader.css';
import preloaderImg from '../assets/preloader.webp';

const SCRIBBLE_COLORS = [
  'var(--v2-color-purple)',
  'var(--v2-color-orange)',
  '#4b69f0',
  '#82a0ff',
  '#29725f',
  '#a0325a',
  '#f0befa'
];

const SCRIBBLE_PATH = "M299.654 453.865C505.574 319.225 711.494 184.585 836.054 109.945C960.614 35.3048 997.574 24.7448 944.014 110.385C890.454 196.025 745.254 378.185 571.454 634.385C397.654 890.585 199.654 1215.3 110.854 1382.58C22.0544 1549.86 48.4544 1549.86 77.8944 1540.62C107.334 1531.38 139.014 1512.9 367.854 1319.9C596.694 1126.9 1021.73 759.945 1255.21 555.065C1488.69 350.185 1517.73 318.505 1527.41 306.145C1537.09 293.785 1526.53 301.705 1346.85 618.625C1167.17 935.545 818.694 1561.22 635.214 1896.74C451.734 2232.26 443.814 2258.66 447.654 2268.3C451.494 2277.94 467.334 2270.02 511.134 2236.9C554.934 2203.78 626.214 2145.7 966.534 1817.46C1306.85 1489.22 1914.05 892.585 2263.81 557.505C2613.57 222.425 2687.49 166.985 2741.41 129.185C2795.33 91.3848 2827.01 72.9048 2843.33 67.3448C2859.65 61.7848 2859.65 69.7048 2849.09 96.2248C2838.53 122.745 2817.41 167.625 2584.77 544.505C2352.13 921.385 1370.37 2165.43 1139.25 2537.83C908.134 2910.23 902.854 2926.07 902.774 2939.51C902.694 2952.95 907.974 2963.51 1255.21 2613.87C1602.45 2264.23 2829.73 1017.54 2903.53 1071.46C2977.33 1125.38 2176.12 2817.04 2128 3037C2079.88 3256.96 2911.24 2018.56 3172 1793";

const ANIMATION_CONFIG = {
  strokeWidthStart: '8%',
  strokeWidthMax: '31%',
  scale: 0.7,
  durationIn: 1.8,
  durationOut: 2.0,
  imageHold: 1.5
};

const Preloader = ({ onComplete }) => {
  const svgRef = useRef(null);
  const pathRef = useRef(null);
  const pathRef2 = useRef(null);
  const logoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const svgEl = svgRef.current;
    const pathEl = pathRef.current;
    const logoEl = logoRef.current;
    const containerEl = containerRef.current;

    if (!svgEl || !pathEl || !logoEl || !containerEl) return;

    // Auto-run scribble animation after short delay
    const timer = setTimeout(() => {
      runScribbleAnimation();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  function runScribbleAnimation() {
    const svgEl = svgRef.current;
    const pathEl = pathRef.current;
    const pathEl2 = pathRef2.current;
    const logoEl = logoRef.current;
    const containerEl = containerRef.current;

    if (!svgEl || !pathEl || !logoEl || !containerEl) return;

    const config = ANIMATION_CONFIG;
    const durIn = config.durationIn;
    const durOut = config.durationOut;

    // Set specific purple color
    svgEl.style.color = '#663FF2';

    // Determine logo color based on background lightness
    // Since color is fixed to purple, logo should be white to contrast
    const logoColor = '#ffffff';

    // Get path length
    const pathLength = pathEl.getTotalLength();
    const l = pathLength + 5;

    // Set initial state
    gsap.set(pathEl, {
      strokeDasharray: l,
      strokeDashoffset: l,
      strokeWidth: config.strokeWidthStart,
      opacity: 1
    });
    gsap.set(pathEl2, {
      strokeDasharray: l,
      strokeDashoffset: l,
      strokeWidth: config.strokeWidthStart,
      opacity: 1
    });
    gsap.set(svgEl, { opacity: 1, scale: config.scale, x: 0, y: 0, rotation: 0 });
    gsap.set(logoEl, { opacity: 0, scale: 1 });
    gsap.set(containerEl, { opacity: 1 });

    // Set logo color
    logoEl.style.color = logoColor;

    // Create timeline
    const tl = gsap.timeline({
      onComplete: () => {
        // Fade out entire preloader
        gsap.to(containerEl, {
          opacity: 0,
          duration: 0.4,
          ease: 'power2.inOut',
          onComplete: () => {
            onComplete?.();
          }
        });
      }
    });

    // Draw in (Scribble fills the screen)
    tl.to(pathEl, { strokeDashoffset: 0, duration: durIn, ease: 'power1.inOut' }, 0);
    tl.to(pathEl, { strokeWidth: config.strokeWidthMax, duration: durIn, ease: 'power2.inOut' }, 0);

    // Fade in image AFTER scribble has filled the screen
    tl.set(logoEl, { autoAlpha: 0 }, 0);
    tl.to(logoEl, {
      autoAlpha: 1,
      duration: 0.5,
      ease: 'power2.out'
    }, durIn);

    // Draw second scribble over everything
    const undrawStart = durIn + config.imageHold;
    tl.to(pathEl2, { strokeDashoffset: 0, duration: durOut, ease: 'power1.inOut' }, undrawStart);
    tl.to(pathEl2, { strokeWidth: config.strokeWidthMax, duration: durOut, ease: 'power2.inOut' }, undrawStart);
  }

  return (
    <div ref={containerRef} className="preloader-container">
      {/* Center Logo/Text */}
      <div ref={logoRef} className="preloader-logo" style={{ width: '100vw', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', opacity: 0 }}>
        <div style={{ width: '100%', height: '100%' }}>
          <img src={preloaderImg} alt="qalvin.dev" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      </div>

      {/* Scribble SVG */}
      <svg
        ref={svgRef}
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        viewBox="0 0 3222 3114"
        fill="none"
        preserveAspectRatio="none"
        className="preloader-scribble"
      >
        <path
          ref={pathRef}
          d={SCRIBBLE_PATH}
          stroke="currentColor"
          strokeLinecap="round"
          style={{
            strokeWidth: '0%',
            strokeDashoffset: '0.001',
            strokeDasharray: '0px, 999999px'
          }}
        />
      </svg>

      {/* Second Scribble SVG (Draws over logo in #fbf9ef) */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        viewBox="0 0 3222 3114"
        fill="none"
        preserveAspectRatio="none"
        className="preloader-scribble"
        style={{ zIndex: 100001 }}
      >
        <path
          ref={pathRef2}
          d={SCRIBBLE_PATH}
          stroke="#fbf9ef"
          strokeLinecap="round"
          style={{
            strokeWidth: '0%',
            strokeDashoffset: '0.001',
            strokeDasharray: '0px, 999999px'
          }}
        />
      </svg>
    </div>
  );
};

export default Preloader;
